// src/pages/admin/JobDescriptions.js
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import {
  DocumentTextIcon,
  PlusIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  DocumentArrowUpIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const JobDescriptions = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInterviewProgressModal, setShowInterviewProgressModal] = useState(false);
  const [interviewCreationData, setInterviewCreationData] = useState(null);
  const [selectedJobForQuestions, setSelectedJobForQuestions] = useState(null);
  const [jobQuestions, setJobQuestions] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const queryClient = useQueryClient();

  // Fetch job descriptions
  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['job-descriptions'],
    queryFn: () => apiService.getJobDescriptions(),
  });

  // Create job description mutation
  const createJobMutation = useMutation({
    mutationFn: async (data) => {
      try {
        // Immediate feedback
        toast.loading('Starting job description and interview creation...', { id: 'creation-progress' });
        
        // Step 1: Create the job description
        console.log('Creating job description with data:', data);
        toast.loading('Creating job description...', { id: 'creation-progress' });
        const jobResponse = await apiService.createJobDescription(data);
        console.log('Job description response:', jobResponse);
        
        // Validate job description response
        // Handle different response structures
        let jobDescription;
        if (jobResponse.data) {
          jobDescription = jobResponse.data;
        } else if (jobResponse.id) {
          // Response is the job description object directly
          jobDescription = jobResponse;
        } else {
          console.error('Unexpected job description response structure:', jobResponse);
          throw new Error('Invalid job description response structure');
        }
        
        // Validate job description has required fields
        if (!jobDescription.id) {
          throw new Error('Job description ID not found in response');
        }
        
        console.log('Job description created successfully:', jobDescription);
        
        // Switch to interview modal immediately after JD creation
        const tempResult = {
          jobDescription,
          interview: null,
          questions: []
        };
        
        // Show the interview progress modal immediately
        setShowCreateModal(false);
        setInterviewCreationData(tempResult);
        setShowInterviewProgressModal(true);
        
        // Extract job description text for AI question generation and interview creation
        let jobDescriptionText = '';
        let jobTitle = '';
        
        if (data instanceof FormData) {
          // If file upload, use the description from form data or extracted text
          jobDescriptionText = data.get('description') || jobDescription?.extracted_text || jobDescription?.description;
          jobTitle = data.get('title') || jobDescription?.title;
        } else {
          // If manual entry, use the provided description
          jobDescriptionText = data.description;
          jobTitle = data.title;
        }
        
        console.log('Extracted job title:', jobTitle);
        console.log('Extracted job description text length:', jobDescriptionText?.length || 0);
        
        // Step 2: Create interview based on job description
        const interviewData = {
          title: `Interview - ${jobTitle}`,
          description: `Interview for ${jobTitle} position`,
          job_description_id: jobDescription.id,
          duration_minutes: 60,
          ai_generated_questions: 5,
          custom_questions_only: false,
          custom_questions: []
        };
        
        console.log('🔍 DEBUG: Creating interview with data:', interviewData);
        console.log('🔍 DEBUG: ai_generated_questions set to:', interviewData.ai_generated_questions);
        const interviewResponse = await apiService.createInterview(interviewData);
        console.log('🔍 DEBUG: Interview creation response:', interviewResponse);
        console.log('🔍 DEBUG: Interview response type:', typeof interviewResponse);
        console.log('🔍 DEBUG: Interview response keys:', Object.keys(interviewResponse || {}));
        
        // Validate interview response
        let interview;
        if (interviewResponse.data) {
          interview = interviewResponse.data;
        } else if (interviewResponse.interview) {
          interview = interviewResponse.interview;
        } else {
          interview = interviewResponse;
        }
        
        if (!interview || !interview.id) {
          console.error('Invalid interview response structure:', interviewResponse);
          console.error('Expected interview object with id, got:', interview);
          throw new Error('Failed to create interview - missing interview ID in response');
        }
        
        console.log('🔍 DEBUG: Interview created successfully:', interview);
        console.log('🔍 DEBUG: Interview ID:', interview.id);
        toast.loading('Checking interview questions...', { id: 'creation-progress' });
      
      // Step 3: Get questions that were automatically generated during interview creation
      let questions = [];
      
      console.log('🔍 DEBUG: Getting questions from interview response...');
      
      // First try to get questions directly from interview creation response
      if (interviewResponse.questions && Array.isArray(interviewResponse.questions)) {
        questions = interviewResponse.questions;
        console.log('🔍 DEBUG: Got questions from interview response:', questions.length);
      } else {
        console.log('🔍 DEBUG: No questions in response, fetching from API...');
        
        // The backend should have automatically generated questions during interview creation
        // Let's fetch them now
        try {
          // Small delay to ensure backend processing is complete
          await new Promise(resolve => setTimeout(resolve, 1000));
        
        const questionsResponse = await apiService.getInterviewQuestions(interview.id);
        console.log('🔍 DEBUG: Questions response:', questionsResponse);
        console.log('🔍 DEBUG: Questions response type:', typeof questionsResponse);
        console.log('🔍 DEBUG: Is array?:', Array.isArray(questionsResponse));
        
        // Handle different response formats
        if (Array.isArray(questionsResponse)) {
          questions = questionsResponse;
        } else if (questionsResponse.data && Array.isArray(questionsResponse.data)) {
          questions = questionsResponse.data;
        } else if (questionsResponse.questions && Array.isArray(questionsResponse.questions)) {
          questions = questionsResponse.questions;
        } else {
          questions = [];
        }
        
        console.log('🔍 DEBUG: Retrieved questions count:', questions.length);
        
        // If no questions were generated automatically, generate them now
        if (questions.length === 0) {
          console.log('🔍 DEBUG: No questions found, generating now...');
          const genResponse = await apiService.generateAIQuestions(interview.id, 5, 'technical');
          console.log('🔍 DEBUG: Generated questions response:', genResponse);
          
          // Fetch questions again after generation
          const finalQuestionsResponse = await apiService.getInterviewQuestions(interview.id);
          questions = finalQuestionsResponse.data || [];
          console.log('🔍 DEBUG: Final questions count after generation:', questions.length);
        }
        } catch (error) {
          console.error('🔍 DEBUG: Error fetching/generating questions:', error);
        }
      }
      
        console.log('🔍 DEBUG: Final questions count:', questions.length);
        console.log('🔍 DEBUG: Final questions:', questions);
        
        // Dismiss loading toast
        toast.dismiss('creation-progress');
        
        return {
          jobDescription,
          interview,
          questions
        };
      } catch (error) {
        console.error('Error in job description/interview creation:', error);
        toast.dismiss('creation-progress');
        throw error;
      }
    },
    onSuccess: (result) => {
      // Update the modal with final data (interview and questions)
      console.log('🔍 DEBUG: Final result for modal:', result);
      setInterviewCreationData(result);
      
      // Show final success notification
      if (result.interview && result.questions.length > 0) {
        toast.success(`Interview created with ${result.questions.length} AI questions!`);
      } else if (result.interview) {
        toast.success('Interview created successfully!');
      }
      
      queryClient.invalidateQueries(['job-descriptions']);
      queryClient.invalidateQueries(['admin-interviews']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create job description and interview');
    },
  });

  // Delete job description handlers
  const handleDeleteJobDescription = (job) => {
    setJobToDelete(job);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!jobToDelete) return;
    
    setDeleteLoading(true);
    try {
      await apiService.deleteJobDescription(jobToDelete.id);
      toast.success('Job description and all related data deleted successfully');
      queryClient.invalidateQueries(['job-descriptions']);
      setShowDeleteModal(false);
      setJobToDelete(null);
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.error || 'Failed to delete job description');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setJobToDelete(null);
  };

  // Handle manage questions
  const handleManageQuestions = async (job) => {
    setSelectedJobForQuestions(job);
    
    // Try to generate questions if description exists
    if (job.description || job.extracted_text) {
      try {
        const jobText = job.extracted_text || job.description;
        const questionsResponse = await apiService.generateQuestionsFromJobDescription(jobText, 5);
        setJobQuestions(questionsResponse.questions || []);
      } catch (error) {
        console.error('Failed to generate questions:', error);
        setJobQuestions([]);
      }
    } else {
      setJobQuestions([]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Descriptions</h1>
          <p className="text-gray-600">Manage job postings and requirements.</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Job Description
        </button>
      </div>

      {/* Job Descriptions List */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : jobsData?.job_descriptions?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobsData.job_descriptions.map((job) => (
              <div key={job.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-8 w-8 text-primary-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{job.title}</h3>
                      <p className="text-sm text-gray-500 flex items-center">
                        <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                        {job.company}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {job.description?.substring(0, 150)}...
                  </p>
                </div>

                {/* Show file upload indicator if job was created from file */}
                {job.file_path && (
                  <div className="mb-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                      <DocumentArrowUpIcon className="h-3 w-3 mr-1" />
                      Uploaded from file
                    </span>
                  </div>
                )}

                {/* Interview Status */}
                <div className="mb-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Interview Status:</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Manage questions from the Interview Management tab
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {new Date(job.created_at).toLocaleDateString()}
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    job.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {job.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Action buttons */}
                <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
                  <button
                    onClick={() => handleDeleteJobDescription(job)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <TrashIcon className="h-3 w-3 mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No job descriptions found</p>
            <p className="text-sm text-gray-400">Create your first job description to get started</p>
          </div>
        )}
      </div>

      {/* Interview Progress Modal */}
      {showInterviewProgressModal && interviewCreationData && (
        <InterviewProgressModal
          data={interviewCreationData}
          onClose={() => {
            setShowInterviewProgressModal(false);
            setInterviewCreationData(null);
          }}
        />
      )}

      {/* Questions Management Modal */}
      {selectedJobForQuestions && (
        <JobQuestionsModal
          job={selectedJobForQuestions}
          questions={jobQuestions}
          onClose={() => {
            setSelectedJobForQuestions(null);
            setJobQuestions([]);
          }}
          onQuestionsUpdate={setJobQuestions}
        />
      )}

      {/* Create Job Description Modal */}
      {showCreateModal && (
        <CreateJobModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createJobMutation.mutate(data)}
          isLoading={createJobMutation.isLoading}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && jobToDelete && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Delete Job Description</h3>
            </div>
            
            <div className="px-6 py-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <TrashIcon className="h-10 w-10 text-red-600" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">
                    "{jobToDelete.title}"
                  </h4>
                  <p className="text-sm text-gray-500">
                    {jobToDelete.company}
                  </p>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Warning: This action cannot be undone
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>This will permanently delete:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>The job description and all its data</li>
                        <li>All associated resumes and files</li>
                        <li>All related interviews and questions</li>
                        <li>All interview sessions and candidate responses</li>
                        <li>All interview links and ratings</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                Are you sure you want to delete this job description and all related data?
              </p>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                disabled={deleteLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </div>
                ) : (
                  'Delete Job Description'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Job Description Modal
const CreateJobModal = ({ onClose, onSubmit, isLoading }) => {
  const [uploadMethod, setUploadMethod] = useState('manual'); // 'manual' or 'file'
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingSteps, setLoadingSteps] = useState({
    jobDescription: false,
    interview: false,
    questions: false
  });
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    requirements: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (uploadMethod === 'file' && selectedFile) {
      // Create FormData for file upload
      const fileFormData = new FormData();
      fileFormData.append('file', selectedFile);
      fileFormData.append('title', formData.title);
      fileFormData.append('company', formData.company);
      fileFormData.append('requirements', formData.requirements);
      
      onSubmit(fileFormData);
    } else {
      // Submit as JSON for manual entry
      onSubmit(formData);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a PDF, DOC, DOCX, or TXT file');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Auto-fill title if empty
      if (!formData.title) {
        const fileName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
        setFormData(prev => ({ ...prev, title: fileName }));
      }
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    // Reset file input
    const fileInput = document.getElementById('job-file-input');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Create Job & Interview</h2>
              <p className="text-sm text-gray-600 mt-1">
                This will create a job description, interview, and generate 5 AI questions automatically.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Upload Method Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              How would you like to create the job description and interview?
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setUploadMethod('manual')}
                className={`flex-1 p-2 border rounded-lg transition-colors ${
                  uploadMethod === 'manual'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <DocumentTextIcon className="h-6 w-6 mx-auto mb-2" />
                <div className="text-sm font-medium">Manual Entry</div>
                <div className="text-xs text-gray-500">Type details manually</div>
              </button>
              
              <button
                type="button"
                onClick={() => setUploadMethod('file')}
                className={`flex-1 p-4 border rounded-lg transition-colors ${
                  uploadMethod === 'file'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <DocumentArrowUpIcon className="h-6 w-6 mx-auto mb-2" />
                <div className="text-sm font-medium">Upload File</div>
                <div className="text-xs text-gray-500">Auto-extract from file</div>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-2">
            {/* File Upload Section */}
            {uploadMethod === 'file' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Job Description File
                </label>
                
                {!selectedFile ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-gray-400 transition-colors">
                    <DocumentArrowUpIcon className="h-6 w-6 text-gray-400 mx-auto mb-4" />
                    <div className="text-sm text-gray-600 mb-2">
                      <label htmlFor="job-file-input" className="cursor-pointer">
                        <span className="text-primary-600 hover:text-primary-500">Click to upload</span>
                        {' '}or drag and drop
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">PDF, DOC, DOCX, TXT up to 10MB</p>
                    <input
                      id="job-file-input"
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.txt"
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <DocumentTextIcon className="h-8 w-8 text-primary-600 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                          <p className="text-xs text-gray-500">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Basic Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title {uploadMethod === 'file' && '(Optional - will use filename if empty)'}
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required={uploadMethod === 'manual'}
                className="input-field"
                placeholder="e.g., Senior Software Engineer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="input-field"
                placeholder="e.g., Tech Corp Inc."
              />
            </div>

            {/* Manual Entry Fields */}
            {uploadMethod === 'manual' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={2}
                    className="input-field"
                    placeholder="Detailed job description, responsibilities, and role overview..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Key Requirements
                  </label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    rows={2}
                    className="input-field"
                    placeholder="Enter keywords relevant for profile..."
                  />
                </div>
              </>
            )}

            {/* File Upload Additional Fields */}
            {uploadMethod === 'file' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key Requirements
                </label>
                <textarea
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  rows={3}
                  className="input-field"
                  placeholder="Enter keywords relevant for profile..."
                />
              </div>
            )}

            {/* Loading Progress */}
            {isLoading && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <div className="text-sm font-medium text-gray-700 mb-3">Creating your interview setup...</div>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-5 h-5 mr-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                    </div>
                    <span className="text-sm text-gray-600">Creating job description</span>
                  </div>
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-5 h-5 mr-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                    </div>
                    <span className="text-sm text-gray-600">Setting up interview</span>
                  </div>
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-5 h-5 mr-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                    </div>
                    <span className="text-sm text-gray-600">Generating AI questions</span>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  This may take a few moments while we analyze your job description and generate relevant interview questions.
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="btn-secondary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || (uploadMethod === 'file' && !selectedFile)}
                className="btn-primary disabled:opacity-50 min-w-[180px]"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </div>
                ) : (
                  'Create Job & Interview'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Job Questions Management Modal
const JobQuestionsModal = ({ job, questions, onClose, onQuestionsUpdate }) => {
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    question_type: 'technical',
    skill_assessed: ''
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [localQuestions, setLocalQuestions] = useState(questions);

  useEffect(() => {
    setLocalQuestions(questions);
  }, [questions]);

  const handleStartEdit = (index, question) => {
    setEditingQuestionIndex(index);
    setEditedText(question.question || question.question_text || '');
  };

  const handleSaveEdit = (index) => {
    if (editedText.trim()) {
      const updatedQuestions = [...localQuestions];
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        question: editedText,
        question_text: editedText
      };
      setLocalQuestions(updatedQuestions);
      onQuestionsUpdate(updatedQuestions);
      setEditingQuestionIndex(null);
      setEditedText('');
      toast.success('Question updated successfully');
    }
  };

  const handleCancelEdit = () => {
    setEditingQuestionIndex(null);
    setEditedText('');
  };

  const handleDeleteQuestion = (index) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      const updatedQuestions = localQuestions.filter((_, i) => i !== index);
      setLocalQuestions(updatedQuestions);
      onQuestionsUpdate(updatedQuestions);
      toast.success('Question deleted successfully');
    }
  };

  const handleAddQuestion = () => {
    if (newQuestion.question_text.trim() && newQuestion.skill_assessed.trim()) {
      const questionToAdd = {
        question: newQuestion.question_text,
        question_text: newQuestion.question_text,
        question_type: newQuestion.question_type,
        skill_assessed: newQuestion.skill_assessed,
        is_ai_generated: false
      };
      const updatedQuestions = [...localQuestions, questionToAdd];
      setLocalQuestions(updatedQuestions);
      onQuestionsUpdate(updatedQuestions);
      setNewQuestion({ question_text: '', question_type: 'technical', skill_assessed: '' });
      setShowAddForm(false);
      toast.success('Question added successfully');
    }
  };

  const handleRegenerateQuestions = async () => {
    setIsRegenerating(true);
    try {
      const jobText = job.extracted_text || job.description;
      const questionsResponse = await apiService.generateQuestionsFromJobDescription(jobText, 5);
      const newQuestions = questionsResponse.questions || [];
      setLocalQuestions(newQuestions);
      onQuestionsUpdate(newQuestions);
      toast.success(`${newQuestions.length} new questions generated!`);
    } catch (error) {
      console.error('Failed to regenerate questions:', error);
      toast.error('Failed to regenerate questions');
    } finally {
      setIsRegenerating(false);
    }
  };

  const getQuestionTypeColor = (type) => {
    switch (type) {
      case 'technical': return 'bg-blue-100 text-blue-800';
      case 'behavioral': return 'bg-green-100 text-green-800';
      case 'situational': return 'bg-purple-100 text-purple-800';
      case 'custom': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Interview Questions</h3>
            <p className="mt-1 text-sm text-gray-500">{job.title} - {job.company}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Total Questions: {localQuestions.length}
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Question
            </button>
            <button
              onClick={handleRegenerateQuestions}
              disabled={isRegenerating}
              className="flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
              {isRegenerating ? 'Regenerating...' : 'Regenerate AI Questions'}
            </button>
          </div>
        </div>

        {showAddForm && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Question Text</label>
                <textarea
                  value={newQuestion.question_text}
                  onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  placeholder="Enter your question..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    value={newQuestion.question_type}
                    onChange={(e) => setNewQuestion({ ...newQuestion, question_type: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  >
                    <option value="technical">Technical</option>
                    <option value="behavioral">Behavioral</option>
                    <option value="situational">Situational</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Skill Assessed</label>
                  <input
                    type="text"
                    value={newQuestion.skill_assessed}
                    onChange={(e) => setNewQuestion({ ...newQuestion, skill_assessed: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                    placeholder="e.g., Problem Solving"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewQuestion({ question_text: '', question_type: 'technical', skill_assessed: '' });
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddQuestion}
                  disabled={!newQuestion.question_text.trim() || !newQuestion.skill_assessed.trim()}
                  className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  Add Question
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 300px)' }}>
          {localQuestions.length === 0 ? (
            <div className="text-center py-8">
              <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No questions generated yet.</p>
              <p className="text-sm text-gray-400 mt-2">Click "Regenerate AI Questions" to generate questions for this job.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {localQuestions.map((question, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-gray-500">Q{index + 1}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getQuestionTypeColor(question.question_type || question.type)}`}>
                          {question.question_type || question.type || 'technical'}
                        </span>
                        <span className="text-xs text-gray-500">
                          Skill: {question.skill_assessed || question.skill || 'Not specified'}
                        </span>
                        {question.is_ai_generated && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                            AI Generated
                          </span>
                        )}
                      </div>
                      {editingQuestionIndex === index ? (
                        <div className="space-y-2">
                          <textarea
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            rows={3}
                            className="w-full border-gray-300 rounded-md shadow-sm"
                            autoFocus
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleSaveEdit(index)}
                              disabled={!editedText.trim()}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-900">{question.question || question.question_text}</p>
                      )}
                    </div>
                    {editingQuestionIndex !== index && (
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleStartEdit(index, question)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Edit question"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteQuestion(index)}
                          className="text-red-400 hover:text-red-600"
                          title="Delete question"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Interview Progress Modal
const InterviewProgressModal = ({ data, onClose }) => {
  const queryClient = useQueryClient();
  const [questions, setQuestions] = useState(data.questions || []);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(!data.interview); // Loading if no interview yet
  const [showAddForm, setShowAddForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    question_type: 'technical',
    skill_assessed: ''
  });
  const [customQuestions, setCustomQuestions] = useState([]);
  const [editedQuestions, setEditedQuestions] = useState([]);
  const [savedQuestionIds, setSavedQuestionIds] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Update questions when data changes
  useEffect(() => {
    if (data.questions) {
      setQuestions(data.questions);
      // Track which questions are already saved
      const savedIds = new Set(data.questions.filter(q => q.id).map(q => q.id));
      setSavedQuestionIds(savedIds);
    }
    if (data.interview) {
      setIsLoading(false);
    }
  }, [data]);

  const handleStartEdit = (index, question) => {
    setEditingQuestionIndex(index);
    setEditedText(question.question || question.question_text || '');
  };

  const handleSaveEdit = (index) => {
    if (editedText.trim()) {
      const updatedQuestions = [...questions];
      const originalQuestion = updatedQuestions[index];
      
      updatedQuestions[index] = {
        ...originalQuestion,
        question: editedText,
        question_text: editedText
      };
      
      setQuestions(updatedQuestions);
      
      // Track this as an edited question if it has an ID (existing question)
      if (originalQuestion.id) {
        const editedQuestion = {
          id: originalQuestion.id,
          question_text: editedText,
          originalText: originalQuestion.question || originalQuestion.question_text
        };
        
        setEditedQuestions(prev => {
          const existing = prev.find(eq => eq.id === originalQuestion.id);
          if (existing) {
            // Update existing edited question
            return prev.map(eq => eq.id === originalQuestion.id ? editedQuestion : eq);
          } else {
            // Add new edited question
            return [...prev, editedQuestion];
          }
        });
      } else if (originalQuestion.tempId) {
        // This is a custom question without an ID, update it in the customQuestions array
        setCustomQuestions(prev => {
          return prev.map(cq => {
            // Find the custom question by tempId
            if (cq.tempId === originalQuestion.tempId) {
              return {
                ...cq,
                question: editedText,
                question_text: editedText
              };
            }
            return cq;
          });
        });
      }
      
      setEditingQuestionIndex(null);
      setEditedText('');
      toast.success('Question updated (not saved yet)');
    }
  };

  const handleCancelEdit = () => {
    setEditingQuestionIndex(null);
    setEditedText('');
  };

  const handleDeleteQuestion = async (index) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      const question = questions[index];
      
      try {
        // If the question has an ID and the interview is saved, delete from database
        if (question.id && data.interview?.id) {
          await apiService.deleteQuestion(question.id);
          
          // Refresh the questions from the backend
          queryClient.invalidateQueries(['interview-questions', data.interview.id]);
          queryClient.invalidateQueries(['admin-interviews']);
          toast.success('Question deleted successfully');
        } else {
          // For questions without IDs (newly generated AI questions or custom questions)
          // just remove from local state
          toast.success('Question removed successfully');
        }
        
        // Always remove from local state
        const updatedQuestions = questions.filter((_, i) => i !== index);
        setQuestions(updatedQuestions);
        
        // If it's a custom question without an ID, remove from customQuestions as well
        if (question.is_ai_generated === false && question.tempId) {
          setCustomQuestions(prev => prev.filter(cq => cq.tempId !== question.tempId));
        }
        
      } catch (error) {
        console.error('Error deleting question:', error);
        toast.error('Failed to delete question');
      }
    }
  };

  const handleAddQuestion = () => {
    if (newQuestion.question_text.trim() && newQuestion.skill_assessed.trim()) {
      const tempId = `temp_${Date.now()}_${Math.random()}`; // Generate unique temp ID
      const questionToAdd = {
        tempId: tempId, // Add temporary ID for tracking
        question: newQuestion.question_text,
        question_text: newQuestion.question_text,
        question_type: newQuestion.question_type,
        skill_assessed: newQuestion.skill_assessed,
        is_ai_generated: false
      };
      const updatedQuestions = [...questions, questionToAdd];
      setQuestions(updatedQuestions);
      setCustomQuestions(prev => [...prev, questionToAdd]);
      setNewQuestion({ question_text: '', question_type: 'technical', skill_assessed: '' });
      setShowAddForm(false);
      toast.success('Question added (not saved yet)');
    }
  };

  const handleSaveInterview = async () => {
    setIsSaving(true);
    try {
      if (!data.interview?.id) {
        toast.error('Interview not ready for saving');
        return;
      }

      let changesMade = false;

      // Save all custom questions
      if (customQuestions.length > 0) {
        for (const question of customQuestions) {
          await apiService.addCustomQuestion(data.interview.id, {
            question_text: question.question_text || question.question,
            question_type: question.question_type || 'technical',
            skill_assessed: question.skill_assessed || 'General'
          });
        }
        changesMade = true;
      }
      
      // Save all edited questions
      if (editedQuestions.length > 0) {
        for (const editedQuestion of editedQuestions) {
          await apiService.updateQuestion(editedQuestion.id, {
            question_text: editedQuestion.question_text
          });
        }
        changesMade = true;
      }

      // Save AI-generated questions that don't have IDs yet
      const unsavedAiQuestions = questions.filter(q => 
        q.is_ai_generated !== false && !q.id && !q.tempId && !savedQuestionIds.has(q.id)
      );
      
      if (unsavedAiQuestions.length > 0) {
        for (const question of unsavedAiQuestions) {
          await apiService.addCustomQuestion(data.interview.id, {
            question_text: question.question_text || question.question,
            question_type: question.question_type || 'technical',
            skill_assessed: question.skill_assessed || 'General'
          });
        }
        changesMade = true;
      }
      
      if (!changesMade) {
        toast.info('No changes to save');
        return;
      }

      // Clear only temporary states (not the questions themselves)
      setCustomQuestions([]);
      setEditedQuestions([]);
      
      // Set refreshing state to prevent showing "Generating AI questions" loader
      setIsRefreshing(true);
      
      // Re-fetch the questions to ensure we have the latest data from server
      try {
        const freshQuestions = await apiService.getInterviewQuestions(data.interview.id);
        if (freshQuestions?.data) {
          setQuestions(freshQuestions.data);
          const savedIds = new Set(freshQuestions.data.filter(q => q.id).map(q => q.id));
          setSavedQuestionIds(savedIds);
        } else if (freshQuestions && Array.isArray(freshQuestions)) {
          setQuestions(freshQuestions);
          const savedIds = new Set(freshQuestions.filter(q => q.id).map(q => q.id));
          setSavedQuestionIds(savedIds);
        }
      } catch (error) {
        console.error('Error fetching fresh questions:', error);
      } finally {
        setIsRefreshing(false);
      }
      
      // Refresh caches
      queryClient.invalidateQueries(['interviews']);
      queryClient.invalidateQueries(['interview-questions', data.interview.id]);
      queryClient.invalidateQueries(['admin-interviews']);
      
      toast.success('Interview saved successfully!');
    } catch (error) {
      console.error('Error saving interview:', error);
      toast.error('Failed to save interview');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to check for unsaved changes
  const hasUnsavedChanges = () => {
    // Check for custom questions
    if (customQuestions.length > 0) return true;
    
    // Check for edited questions
    if (editedQuestions.length > 0) return true;
    
    // Check for unsaved AI questions (questions without IDs that aren't custom)
    const unsavedAiQuestions = questions.filter(q => 
      q.is_ai_generated !== false && !q.id && !q.tempId && !savedQuestionIds.has(q.id)
    );
    if (unsavedAiQuestions.length > 0) return true;
    
    return false;
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      const message = customQuestions.length > 0 && editedQuestions.length > 0
        ? `You have ${customQuestions.length} unsaved custom questions and ${editedQuestions.length} unsaved edits. Do you want to discard them?`
        : customQuestions.length > 0
        ? `You have ${customQuestions.length} unsaved custom questions. Do you want to discard them?`
        : `You have ${editedQuestions.length} unsaved edits. Do you want to discard them?`;
      
      if (window.confirm(message)) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const getQuestionTypeColor = (type) => {
    switch (type) {
      case 'technical': return 'bg-blue-100 text-blue-800';
      case 'behavioral': return 'bg-green-100 text-green-800';
      case 'situational': return 'bg-purple-100 text-purple-800';
      case 'custom': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-3"></div>
                    Creating Interview...
                  </>
                ) : (
                  <>
                    <div className="animate-pulse h-2 w-2 bg-green-400 rounded-full mr-3"></div>
                    Interview Created Successfully!
                  </>
                )}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {isLoading ? (
                  'Setting up your interview with AI questions...'
                ) : (
                  `${data.interview?.title} • Review and edit the AI-generated questions below`
                )}
              </p>
              {/* 💡 To reorder questions, use Interview Management tab */}
              <div className="mt-1 text-xs text-blue-600">
                💡 To reorder questions, use Interview Management tab
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">Job Description Created</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                isLoading ? 'bg-yellow-100' : 'bg-green-100'
              }`}>
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                ) : (
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  isLoading ? 'text-yellow-800' : 'text-green-800'
                }`}>
                  {isLoading ? 'Creating Interview...' : 'Interview Created'}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                isLoading || questions.length === 0 ? 'bg-yellow-100' : 'bg-green-100'
              }`}>
                {isLoading || questions.length === 0 ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                ) : (
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  isLoading || questions.length === 0 ? 'text-yellow-800' : 'text-green-800'
                }`}>
                  {isLoading || questions.length === 0 
                    ? 'Generating AI Questions...' 
                    : `${questions.filter(q => q.is_ai_generated !== false).length} AI Questions Generated`
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h4 className="text-md font-medium text-gray-900">Review & Edit Questions</h4>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                {questions.filter(q => q.is_ai_generated !== false).length} AI + {questions.filter(q => q.is_ai_generated === false).length} Custom = {questions.length} total
              </span>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Question
              </button>
            </div>
          </div>
        </div>

        {showAddForm && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Question Text</label>
                <textarea
                  value={newQuestion.question_text}
                  onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  placeholder="Enter your question..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    value={newQuestion.question_type}
                    onChange={(e) => setNewQuestion({ ...newQuestion, question_type: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  >
                    <option value="technical">Technical</option>
                    <option value="behavioral">Behavioral</option>
                    <option value="situational">Situational</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Skill Assessed</label>
                  <input
                    type="text"
                    value={newQuestion.skill_assessed}
                    onChange={(e) => setNewQuestion({ ...newQuestion, skill_assessed: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                    placeholder="e.g., Problem Solving"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewQuestion({ question_text: '', question_type: 'technical', skill_assessed: '' });
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddQuestion}
                  disabled={!newQuestion.question_text.trim() || !newQuestion.skill_assessed.trim()}
                  className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  Add Question
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {questions.length === 0 && !isRefreshing ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Generating AI questions...</p>
            </div>
          ) : isRefreshing ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Refreshing questions...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {questions.map((question, index) => (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-sm font-medium text-gray-500">Q{index + 1}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getQuestionTypeColor(question.question_type || question.type)}`}>
                          {question.question_type || question.type || 'technical'}
                        </span>
                        <span className="text-xs text-gray-500">
                          Skill: {question.skill_assessed || question.skill || 'General'}
                        </span>
                        {question.is_ai_generated !== false ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                            AI Generated
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            Custom
                          </span>
                        )}
                        {question.id && editedQuestions.find(eq => eq.id === question.id) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            Edited
                          </span>
                        )}
                      </div>
                      {editingQuestionIndex === index ? (
                        <div className="space-y-3">
                          <textarea
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            rows={3}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            autoFocus
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleSaveEdit(index)}
                              disabled={!editedText.trim()}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-900 text-sm leading-relaxed">
                          {question.question || question.question_text}
                        </div>
                      )}
                    </div>
                    {editingQuestionIndex !== index && (
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleStartEdit(index, question)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title="Edit question"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteQuestion(index)}
                          className="text-red-400 hover:text-red-600 p-1"
                          title="Delete question"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {hasUnsavedChanges() && (
              <span className="text-amber-600 font-medium">
                • {customQuestions.length > 0 && `${customQuestions.length} unsaved custom question${customQuestions.length > 1 ? 's' : ''}`}
                {customQuestions.length > 0 && editedQuestions.length > 0 && ', '}
                {editedQuestions.length > 0 && `${editedQuestions.length} unsaved edit${editedQuestions.length > 1 ? 's' : ''}`}
                {(() => {
                  const unsavedAi = questions.filter(q => 
                    q.is_ai_generated !== false && !q.id && !q.tempId && !savedQuestionIds.has(q.id)
                  ).length;
                  if (unsavedAi > 0) {
                    return (customQuestions.length > 0 || editedQuestions.length > 0 ? ', ' : '') + 
                           `${unsavedAi} unsaved AI question${unsavedAi > 1 ? 's' : ''}`;
                  }
                  return '';
                })()}
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Close
            </button>
            <button
              onClick={handleSaveInterview}
              disabled={isSaving || isLoading || !data.interview || !hasUnsavedChanges()}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                'Save Interview'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDescriptions;